import React, { useState } from 'react';
import { useAuth } from '../App';

export default function Layout({ children }) {
  const { user, logout, navigate } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const NavLink = ({ to, children, className = '' }) => (
    <button
      onClick={() => {
        navigate(to);
        setMobileMenuOpen(false);
      }}
      className={`hover-underline-coral text-navy-700 hover:text-navy-900 transition-colors ${className}`}
    >
      {children}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="bg-white shadow-luxury sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex items-center space-x-3">
              <img 
                src="https://thedeolixacademy.com/wp-content/uploads/2025/12/The-Deolix-Academy-Logo-scaled.png" 
                alt="The Deolix Academy" 
                className="h-12 w-auto"
              />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {user ? (
                <>
                  {user.isAdmin ? (
                    <>
                      <NavLink to="/admin">Dashboard</NavLink>
                      <NavLink to="/admin/advisors">Advisors</NavLink>
                      <NavLink to="/admin/leads">Leads</NavLink>
                    </>
                  ) : (
                    <>
                      <NavLink to="/dashboard">Dashboard</NavLink>
                      <NavLink to="/my-leads">My Leads</NavLink>
                      <NavLink to="/training">Training</NavLink>
                      <NavLink to="/purchase">Purchase</NavLink>
                    </>
                  )}
                  <div className="flex items-center space-x-4 ml-4 pl-4 border-l border-cream-300">
                    <span className="text-sm text-navy-600 font-body">
                      {user.firstName} {user.lastName}
                    </span>
                    <button onClick={logout} className="btn-ghost text-red-600 hover:text-red-700">
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <NavLink to="/login">Login</NavLink>
                  <button onClick={() => navigate('/register')} className="btn-primary">
                    Get Started
                  </button>
                </>
              )}
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-navy-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-cream-200">
              <div className="flex flex-col space-y-4">
                {user ? (
                  <>
                    {user.isAdmin ? (
                      <>
                        <NavLink to="/admin" className="py-2">Dashboard</NavLink>
                        <NavLink to="/admin/advisors" className="py-2">Advisors</NavLink>
                        <NavLink to="/admin/leads" className="py-2">Leads</NavLink>
                      </>
                    ) : (
                      <>
                        <NavLink to="/dashboard" className="py-2">Dashboard</NavLink>
                        <NavLink to="/my-leads" className="py-2">My Leads</NavLink>
                        <NavLink to="/training" className="py-2">Training</NavLink>
                        <NavLink to="/purchase" className="py-2">Purchase</NavLink>
                      </>
                    )}
                    <button onClick={logout} className="py-2 text-left text-red-600">
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <NavLink to="/login" className="py-2">Login</NavLink>
                    <NavLink to="/register" className="py-2">Register</NavLink>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-navy-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-display text-xl mb-4">The Deolix Academy</h3>
              <p className="text-navy-300 text-sm font-body leading-relaxed">
                Training the next generation of luxury travel advisors. 
                Our Unlimited Luxury Lead Program connects you with travelers 
                seeking premium experiences.
              </p>
            </div>
            <div>
              <h4 className="font-body uppercase tracking-wider text-sm mb-4 text-navy-300">Quick Links</h4>
              <ul className="space-y-2 text-navy-300 text-sm font-body">
                <li>
                  <a href="https://thedeolixacademy.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Main Website
                  </a>
                </li>
                <li>
                  <a href="https://courses.thedeolixacademy.com/login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Course Login
                  </a>
                </li>
                <li>
                  <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                    Advisor Portal
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-body uppercase tracking-wider text-sm mb-4 text-navy-300">Contact</h4>
              <p className="text-navy-300 text-sm font-body">
                Have questions about the lead program?<br />
                <a href="mailto:leads@thedeolixacademy.com" className="text-coral-400 hover:text-coral-300 transition-colors">
                  leads@thedeolixacademy.com
                </a>
              </p>
            </div>
          </div>
          <div className="border-t border-navy-800 mt-10 pt-8 text-center text-navy-500 text-sm font-body">
            <p>&copy; {new Date().getFullYear()} The Deolix Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
